import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTemplate, useUpdateTemplate, TokenStandard } from '../../../hooks/useSmartContractTemplates';
import { useVersions, useActivateVersion } from '../../../hooks/useTemplateVersions';
import { useAuditDetails } from '../../../hooks/useTemplateAudit';
import { SolidityCodeViewer, AuditStatusBadge } from '../../../components/smart-contracts';
import { PageHeader } from '../../../components/help/PageHeader';
import { Badge } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button';
import { 
  ArrowLeft, 
  Edit, 
  Save, 
  X, 
  Shield, 
  GitBranch, 
  Clock,
  ExternalLink,
  FileCode,
  CheckCircle,
  AlertTriangle
} from 'lucide-react';
import { useAuthStore } from '../../../stores/auth.store';

const tokenStandardLabels: Record<TokenStandard, string> = {
  ERC20: 'ERC-20 (Fungível)',
  ERC1400: 'ERC-1400 (Security Token)',
  ERC4626: 'ERC-4626 (Vault)',
};

export function SmartContractTemplateDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, isSuperAdmin } = useAuthStore();
  const isSuperAdminUser = isSuperAdmin();

  const [isEditing, setIsEditing] = useState(false);
  const [editedCode, setEditedCode] = useState('');
  const [editedName, setEditedName] = useState('');
  const [editedDescription, setEditedDescription] = useState('');

  const { data: template, isLoading, error } = useTemplate(id);
  const { data: versions } = useVersions(id);
  const { data: auditDetails } = useAuditDetails(id);
  const updateMutation = useUpdateTemplate();
  const activateVersionMutation = useActivateVersion();

  const handleStartEdit = () => {
    if (template) {
      setEditedCode(template.sourceCode);
      setEditedName(template.name);
      setEditedDescription(template.description || '');
      setIsEditing(true);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedCode('');
    setEditedName('');
    setEditedDescription('');
  };

  const handleSave = async () => {
    if (!id) return;
    
    await updateMutation.mutateAsync({
      id,
      dto: {
        name: editedName,
        description: editedDescription,
        sourceCode: editedCode !== template?.sourceCode ? editedCode : undefined,
      },
    });
    setIsEditing(false);
  };

  const handleActivateVersion = async (versionId: string) => {
    if (!id) return;
    if (confirm('Tem certeza que deseja ativar esta versão? O código atual será substituído.')) {
      await activateVersionMutation.mutateAsync({ templateId: id, versionId });
    }
  };

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (error || !template) {
    return (
      <div className="p-6">
        <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-4 text-red-400">
          Template não encontrado
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/tokenization/contracts')}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            {isEditing ? (
              <input
                type="text"
                value={editedName}
                onChange={(e) => setEditedName(e.target.value)}
                className="text-2xl font-bold bg-surface-900 border border-surface-700 rounded px-2 py-1 text-surface-100"
              />
            ) : (
              <h1 className="text-2xl font-bold text-surface-100">{template.name}</h1>
            )}
            <div className="flex items-center gap-2 mt-1">
              <Badge variant="info">{tokenStandardLabels[template.tokenStandard]}</Badge>
              <span className="text-surface-400">v{template.currentVersion}</span>
              <AuditStatusBadge
                status={template.auditStatus}
                auditorName={template.auditorCompany}
                auditDate={template.auditDate}
                showDetails
              />
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {isSuperAdminUser && (
            <>
              {isEditing ? (
                <>
                  <Button variant="ghost" onClick={handleCancelEdit}>
                    <X className="w-4 h-4 mr-2" />
                    Cancelar
                  </Button>
                  <Button onClick={handleSave} disabled={updateMutation.isPending}>
                    <Save className="w-4 h-4 mr-2" />
                    Salvar
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="outline" onClick={() => navigate(`/admin/tokenization/contracts/${id}/audit`)}>
                    <Shield className="w-4 h-4 mr-2" />
                    Auditoria
                  </Button>
                  <Button onClick={handleStartEdit}>
                    <Edit className="w-4 h-4 mr-2" />
                    Editar
                  </Button>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* Description */}
      {isEditing ? (
        <textarea
          value={editedDescription}
          onChange={(e) => setEditedDescription(e.target.value)}
          placeholder="Descrição do template..."
          className="w-full px-4 py-2 bg-surface-900 border border-surface-700 rounded-lg text-surface-100 placeholder-surface-500 resize-none"
          rows={2}
        />
      ) : template.description ? (
        <p className="text-surface-400">{template.description}</p>
      ) : null}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Code Viewer */}
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-surface-800 rounded-lg border border-surface-700 overflow-hidden">
            <div className="px-4 py-3 border-b border-surface-700 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileCode className="w-5 h-5 text-primary-400" />
                <h2 className="font-medium text-surface-100">Código Fonte</h2>
              </div>
              {template.documentationUrl && (
                <a
                  href={template.documentationUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-primary-400 hover:text-primary-300 flex items-center gap-1"
                >
                  Documentação
                  <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
            <SolidityCodeViewer
              code={isEditing ? editedCode : template.sourceCode}
              readOnly={!isEditing}
              onCodeChange={setEditedCode}
              height="500px"
            />
          </div>

          {/* Code Hash */}
          <div className="bg-surface-800 rounded-lg p-4 border border-surface-700">
            <div className="flex items-center justify-between">
              <span className="text-sm text-surface-400">Hash do Código (SHA-256)</span>
              {auditDetails?.codeIntegrity ? (
                <div className="flex items-center gap-1 text-green-400">
                  <CheckCircle className="w-4 h-4" />
                  <span className="text-sm">Integridade verificada</span>
                </div>
              ) : template.auditStatus === 'AUDITED' ? (
                <div className="flex items-center gap-1 text-yellow-400">
                  <AlertTriangle className="w-4 h-4" />
                  <span className="text-sm">Código modificado após auditoria</span>
                </div>
              ) : null}
            </div>
            <code className="text-xs text-surface-300 font-mono break-all mt-2 block">
              {template.codeHash}
            </code>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Audit Info */}
          <div className="bg-surface-800 rounded-lg border border-surface-700 overflow-hidden">
            <div className="px-4 py-3 border-b border-surface-700 flex items-center gap-2">
              <Shield className="w-5 h-5 text-primary-400" />
              <h2 className="font-medium text-surface-100">Auditoria</h2>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <span className="text-sm text-surface-400">Status</span>
                <div className="mt-1">
                  <AuditStatusBadge status={template.auditStatus} />
                </div>
              </div>
              {template.auditorCompany && (
                <div>
                  <span className="text-sm text-surface-400">Auditor</span>
                  <p className="text-surface-100">{template.auditorCompany}</p>
                </div>
              )}
              {template.auditDate && (
                <div>
                  <span className="text-sm text-surface-400">Data</span>
                  <p className="text-surface-100">
                    {new Date(template.auditDate).toLocaleDateString('pt-BR')}
                  </p>
                </div>
              )}
              {template.auditReportUrl && (
                <a
                  href={template.auditReportUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-primary-400 hover:text-primary-300 text-sm"
                >
                  Ver relatório
                  <ExternalLink className="w-3 h-3" />
                </a>
              )}
              {template.auditFindings && (
                <div>
                  <span className="text-sm text-surface-400">Observações</span>
                  <p className="text-surface-300 text-sm mt-1">{template.auditFindings}</p>
                </div>
              )}
            </div>
          </div>

          {/* Versions */}
          <div className="bg-surface-800 rounded-lg border border-surface-700 overflow-hidden">
            <div className="px-4 py-3 border-b border-surface-700 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <GitBranch className="w-5 h-5 text-primary-400" />
                <h2 className="font-medium text-surface-100">Versões</h2>
              </div>
              {versions && versions.length > 1 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate(`/admin/tokenization/contracts/${id}/compare`)}
                >
                  Comparar
                </Button>
              )}
            </div>
            <div className="max-h-64 overflow-y-auto">
              {versions?.map((version) => (
                <div
                  key={version.id}
                  className={`px-4 py-3 border-b border-surface-700 last:border-b-0 ${
                    version.version === template.currentVersion ? 'bg-primary-500/10' : ''
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-surface-100">v{version.version}</span>
                        {version.version === template.currentVersion && (
                          <Badge variant="primary">Atual</Badge>
                        )}
                        <AuditStatusBadge status={version.auditStatus} />
                      </div>
                      <div className="flex items-center gap-1 text-xs text-surface-400 mt-1">
                        <Clock className="w-3 h-3" />
                        {new Date(version.createdAt).toLocaleDateString('pt-BR')}
                      </div>
                    </div>
                    {isSuperAdminUser && version.version !== template.currentVersion && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleActivateVersion(version.id)}
                      >
                        Ativar
                      </Button>
                    )}
                  </div>
                  {version.changelog && (
                    <p className="text-sm text-surface-400 mt-2">{version.changelog}</p>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Parameter Schema */}
          {template.parameterSchema && (template.parameterSchema as any[]).length > 0 && (
            <div className="bg-surface-800 rounded-lg border border-surface-700 overflow-hidden">
              <div className="px-4 py-3 border-b border-surface-700">
                <h2 className="font-medium text-surface-100">Parâmetros</h2>
              </div>
              <div className="p-4 space-y-2">
                {(template.parameterSchema as any[]).map((param: any, index: number) => (
                  <div key={index} className="flex items-center justify-between text-sm">
                    <div>
                      <span className="text-surface-100">{param.name}</span>
                      {param.required && <span className="text-red-400 ml-1">*</span>}
                    </div>
                    <Badge variant="default">{param.type}</Badge>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default SmartContractTemplateDetail;
